(function () {
  'use strict';

  const { gsap } = window;
  const { iman } = window.Universe;

  class PopupNewsletter {
    constructor() {
      this.init();
    }

    getCookie(name) {
      const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return match ? match[2] : null;
    }

    setCookie(name, expiry) {
      document.cookie = name + '=true; max-age=' + (expiry * 24 * 60 * 60) + '; path=/; SameSite=Lax';
    }

    setup() {
      if (window.location.pathname === '/challenge') return;

      const popup = document.querySelector('.js-popup-newsletter');
      if (!popup) return;

      this.DOM = {};
      this.DOM.popup = popup;
      this.DOM.closeButtons = this.DOM.popup.querySelectorAll('.js-close-button');
      this.DOM.focusableElements = this.DOM.popup.querySelectorAll(
        'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      this.DOM.form = this.DOM.popup.querySelector('.u-newsletter-form');
      this.DOM.submitButton = this.DOM.form ? this.DOM.form.querySelector('button[type="submit"], .u-newsletter-form__submit') : null;
      this.DOM.formRow = this.DOM.form ? this.DOM.form.querySelector('.u-newsletter-form__row') : null;

      if (this.DOM.popup.hasAttribute('data-shopify-editor')) {
        this.isEditorMode = true;
        this.setupShopifyDesignModeListeners();
        return;
      }

      this.setupFormHandler();

      if (this.DOM.form) {
        this.DOM.form.action = this.DOM.form.action.split('#')[0];
      }

      const popupSubmitted = sessionStorage.getItem('popupNewsletterSubmitted');
      sessionStorage.removeItem('popupNewsletterSubmitted');

      if (popupSubmitted && new URLSearchParams(window.location.search).has('customer_posted')) {
        if (window.location.hash) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        window.scrollTo(0, 0);
        requestAnimationFrame(() => { window.scrollTo(0, 0); });

        if (this.DOM.formRow) this.DOM.formRow.style.display = 'none';

        if (!this._hasSuccess()) {
          this.showSuccessMessage();
        }

        this.animate(1, true);
        return;
      }

      if (this._hasError()) {
        this.animate(1, false);
        return;
      }

      if (this._hasSuccess()) {
        this.clearMessages();
      }

      const isClosed2 = this.getCookie('newsletterClosed');

      if (!isClosed2) {
        const showPlace = this.DOM.popup.dataset.place;

        if (showPlace === 'homepage') {
          if (window.location.pathname === '/') this.animate();
        } else {
          this.animate();
        }
      }
    }

    _hasSuccess() {
      return this.DOM.popup.querySelector('.u-newsletter-form__message--success') !== null;
    }

    _hasError() {
      return this.DOM.popup.querySelector('.u-newsletter-form__message--error') !== null;
    }

    close() {
      this.DOM.popup.classList.remove('is-active');
      this.DOM.popup.setAttribute('data-hidden', 'true');

      if (!this.isEditorMode) {
        const expiry = Number(this.DOM.popup.dataset.expiry) || 30;
        this.setCookie('newsletterClosed', expiry);
      }
    }

    restorePreviousFocus() {
      if (
        this.previouslyFocusedElement &&
        this.previouslyFocusedElement !== document.body &&
        document.contains(this.previouslyFocusedElement)
      ) {
        this.previouslyFocusedElement.focus();
      }
    }

    hidePopup() {
      this.DOM.popup.classList.remove('is-active');
      this.DOM.popup.setAttribute('data-hidden', 'true');
    }

    setupFormHandler() {
      if (!this.DOM.form) return;
      this.DOM.form.addEventListener('submit', this.handleFormSubmit);
    }

    clearMessages() {
      if (this.DOM.form) {
        this.DOM.form.querySelectorAll('.u-newsletter-form__message').forEach((msg) => { msg.remove(); });
      }
    }

    showSuccessMessage() {
      if (this.DOM.formRow) {
        this.DOM.formRow.style.display = 'none';
      }

      const jsSuccessMsg = this.DOM.form ? this.DOM.form.querySelector('.js-success-message') : null;
      const successText = (jsSuccessMsg ? jsSuccessMsg.textContent : null) || 'Thank you for subscribing!';

      const message = document.createElement('h3');
      message.className = 'u-newsletter-form__message u-newsletter-form__message--success';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      message.textContent = successText;
      if (this.DOM.form) this.DOM.form.appendChild(message);
    }

    animate(overrideDelay, autoClose = false) {
      this.ctx = gsap.context(() => {
        const delay = overrideDelay != null ? overrideDelay : Number(this.DOM.popup.dataset.delay);

        const showTl = gsap
          .timeline({ delay: delay, defaults: { ease: 'power4', duration: 0.75 } })
          .call(() => {
            this.DOM.popup.classList.add('is-active');
            this.DOM.popup.removeAttribute('data-hidden');
          })
          .from('.popup-newsletter__body', { autoAlpha: 0, yPercent: 50 });

        this.hideTl = gsap
          .timeline({
            paused: true,
            defaults: { ease: 'power4.inOut', duration: 0.75 }
          })
          .to('.popup-newsletter__body', { autoAlpha: 0, yPercent: 50 })
          .call(() => {
            this.close();
            this.restorePreviousFocus();
          });

        if (autoClose) {
          const expiry = Number(this.DOM.popup.dataset.expiry) || 30;
          this.setCookie('newsletterClosed', expiry);
          showTl.call(() => {
            this.autoCloseCall = gsap.delayedCall(4, () => { this.hideTl.play(); });
          });
        }

        const handleClose = () => { this.hideTl.play(); };
        const focusSelector =
          'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const handleKeydown = (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            this.hideTl.play();
            return;
          }

          if (e.key === 'Tab') {
            const visible = Array.prototype.filter.call(
              this.DOM.popup.querySelectorAll(focusSelector),
              (el) => el.offsetParent !== null
            );
            if (visible.length === 0) return;

            const first = visible[0];
            const last = visible[visible.length - 1];

            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        };

        this.previouslyFocusedElement = document.activeElement;

        const closeBtn = this.DOM.popup.querySelector('.popup-newsletter__close');
        if (closeBtn) {
          showTl.call(() => { closeBtn.focus({ preventScroll: true }); });
        } else if (this.DOM.focusableElements.length > 0) {
          showTl.call(() => { this.DOM.focusableElements[0].focus(); });
        }

        const handleOverlayClick = (e) => {
          if (e.target === this.DOM.popup) this.hideTl.play();
        };

        this.DOM.popup.addEventListener('keydown', handleKeydown);

        this.DOM.closeButtons.forEach((button) => {
          button.addEventListener('click', handleClose);
        });

        if (this.DOM.popup.hasAttribute('data-close-on-overlay')) {
          this.DOM.popup.addEventListener('click', handleOverlayClick);
        }

        return () => {
          if (this.DOM && this.DOM.popup) {
            this.DOM.popup.removeEventListener('keydown', handleKeydown);
            this.DOM.popup.removeEventListener('click', handleOverlayClick);
          }
          if (this.DOM && this.DOM.closeButtons) {
            this.DOM.closeButtons.forEach((button) => {
              button.removeEventListener('click', handleClose);
            });
          }
        };
      }, this.DOM.popup);
    }

    setupShopifyDesignModeListeners() {
      if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

      this.handleSectionLoad = (event) => {
        if (!this.isPopupSection(event)) return;

        const popupSection = event.target.classList.contains('js-popup-newsletter')
          ? event.target
          : event.target.querySelector('.js-popup-newsletter');

        if (!popupSection) return;

        if (this.autoCloseCall) this.autoCloseCall.kill();
        if (this.ctx) this.ctx.revert();

        this.DOM.popup = popupSection;
        this.DOM.closeButtons = this.DOM.popup.querySelectorAll('.js-close-button');
        this.DOM.focusableElements = this.DOM.popup.querySelectorAll(
          'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        this.hidePopup();
      };

      this.handleSectionSelect = (event) => {
        if (!this.isPopupSection(event)) return;

        const popupSection = event.target.classList.contains('js-popup-newsletter')
          ? event.target
          : event.target.querySelector('.js-popup-newsletter');

        if (popupSection) {
          this.DOM.popup = popupSection;
          this.DOM.closeButtons = this.DOM.popup.querySelectorAll('.js-close-button');
          this.DOM.focusableElements = this.DOM.popup.querySelectorAll(
            'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
          );
        }

        if (this.autoCloseCall) this.autoCloseCall.kill();
        if (this.ctx) this.ctx.revert();
        this.animate(0);
      };

      this.handleSectionDeselect = (event) => {
        if (!this.isPopupSection(event)) return;

        if (this.autoCloseCall) this.autoCloseCall.kill();
        if (this.ctx) this.ctx.revert();
        this.hidePopup();
      };

      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    isPopupSection(event) {
      const detail = event.detail;
      return (
        event.target.classList.contains('js-popup-newsletter') ||
        event.target.querySelector('.js-popup-newsletter') !== null ||
        (detail && detail.sectionId && detail.sectionId.indexOf('popup-newsletter') !== -1)
      );
    }

    destroy() {
      if (!this.DOM) return;

      if (this.autoCloseCall) this.autoCloseCall.kill();
      if (this.ctx) this.ctx.revert();

      if (this.DOM.form) {
        this.DOM.form.removeEventListener('submit', this.handleFormSubmit);
      }

      if (this.handleSectionSelect) {
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        document.removeEventListener('shopify:section:select', this.handleSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      }

      this.DOM = undefined;
    }

    reinit() {
      this.destroy();
      this.init();
    }

    init() {
      this.handleFormSubmit = () => {
        sessionStorage.setItem('popupNewsletterSubmitted', 'true');
      };
      this.setup();
    }
  }

  function init() {
    if (!iman.get('popupNewsletter')) {
      iman.add('popupNewsletter', new PopupNewsletter());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Universe.PopupNewsletter = PopupNewsletter;
})();
